import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface CostSummary {
  totalMonth: number;
  totalCalls: number;
  totalTokens: number;
  byProvider: Array<{
    provider: string;
    total_cost_usd: number;
    total_tokens: number;
    call_count: number;
  }>;
}

/** Tiny inline sparkline (pure CSS bar chart). */
function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 0.001);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 28 }}>
      {values.slice(-14).map((v, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: `${Math.max(2, (v / max) * 100)}%`,
            background: v > max * 0.8 ? 'var(--sp-warn)' : 'var(--sp-accent)',
            borderRadius: 1,
            opacity: i === values.length - 1 ? 1 : 0.6,
          }}
        />
      ))}
    </div>
  );
}

function CrossTenantCostWidget({ context }: WidgetProps) {
  const [data, setData] = useState<CostSummary | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    if (!context.isDirector) return;

    void engineFetchJson<{
      total_cost_usd?: number;
      total_calls?: number;
      total_tokens?: number;
      by_provider?: CostSummary['byProvider'];
    }>('/costs/rollup?days=30')
      .then((d) => {
        setData({
          totalMonth: d.total_cost_usd ?? 0,
          totalCalls: d.total_calls ?? 0,
          totalTokens: d.total_tokens ?? 0,
          byProvider: d.by_provider ?? [],
        });
        setEndpointMissing(false);
      })
      .catch(() => {
        setEndpointMissing(true);
      });
  }, [context.authToken, context.isDirector]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Cross-Tenant Cost</span>
        <a href="/command" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Full costs
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 2 }}>
              GET /api/v1/costs/rollup
            </div>
            <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginTop: 8, marginBottom: 8 }}>
              Cross-tenant cost aggregation across all tenants. Requires Director auth.
            </div>
            {/* Mock sparkline */}
            <Sparkline values={[1.2, 0.8, 1.5, 2.1, 0.9, 1.3, 1.7, 2.5, 1.1, 0.7, 1.4, 1.8, 2.0, 1.6]} />
            <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 2 }}>14-day trend (mock)</div>
          </div>
        ) : data ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <div>
                <span style={{ fontSize: 24, fontWeight: 700 }}>${data.totalMonth.toFixed(2)}</span>
                <span style={{ fontSize: 12, color: 'var(--sp-text-muted)', marginLeft: 4 }}>30d total</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12 }}>
              <div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>calls</div>
                <div style={{ fontWeight: 600 }}>{data.totalCalls}</div>
              </div>
              <div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>tokens</div>
                <div style={{ fontWeight: 600 }}>{data.totalTokens.toLocaleString()}</div>
              </div>
            </div>

            {data.byProvider.length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 4 }}>
                  By provider
                </div>
                {data.byProvider.slice(0, 4).map((provider) => (
                  <div
                    key={provider.provider}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '2px 0' }}
                  >
                    <span style={{ color: 'var(--sp-text-muted)' }}>{provider.provider}</span>
                    <span className="mono">${provider.total_cost_usd.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading cost data...</div>
        )}
      </div>
    </div>
  );
}

export const crossTenantCostWidget: WidgetDefinition = {
  id: 'cross-tenant-cost',
  name: 'Cross-Tenant Cost',
  description: 'LLM spend aggregated across all tenants.',
  icon: 'bar-chart-3',
  category: 'admin',
  scope: 'director',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  component: CrossTenantCostWidget,
};
